import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { hasSignedInBefore, hadGuestBefore } from '../lib/returningUser';
import { guardarRutaPendiente, borrarRutaPendiente, destinoDespuesDeEntrar, notarSesion } from '../lib/rutaPendiente';
import { esArranqueDeInvitado } from '../lib/arranqueInvitado';
import ArranqueDeInvitado from './ArranqueDeInvitado';
import BootSplash from './BootSplash';
import OfflineBootScreen from './OfflineBootScreen';

// Guards de ruta + gate de arranque (extraídos de App.js en F4.3b para poder
// testearlos herméticamente). Mientras auth resuelve la sesión, los guards
// devuelven null: el hueco visual lo cubre <AuthBootGate /> (logo sobre #111,
// cero spinner — regla F4.3b: o pantalla correcta, o logo).
// post-t7-2c (F1): eso queda para la app; en la web, la ruta con esqueleto lo pinta (ver `ProtectedRoute`).

/* LA DIRECCIÓN DE ADMINISTRADOR (TANDA 12, firma #182).
 *
 * Se exporta porque hasta esta tanda había DOS MECANISMOS conviviendo: éste, por
 * variable de entorno, y el literal escrito en duro dentro de `AdminDashboard.js`
 * y `AdminErrorsPage.js`. Dos copias de la misma regla es una que se puede quedar
 * atrás; ahora esas dos páginas leen de aquí y en `src/` no queda ningún correo
 * escrito a mano (guarda: `lib/correoAdminUnico.tanda12.test.js`).
 *
 * PRECISIÓN, para que el letrero no prometa de más: lo que queda es UN mecanismo,
 * no una única LECTURA. `components/Sidebar.js` y `pages/DiagReadyChainPage.js`
 * siguen leyendo `process.env.REACT_APP_ADMIN_EMAIL` por su cuenta —la misma
 * variable, en el mismo instante del build—, y eso es a propósito: hay guardas
 * que exigen ver esa expresión literal en cada uno de esos dos archivos, porque
 * ahí lo que se vigila es que el candado no se cierre sobre un literal. Unificar
 * las tres lecturas sería otra tanda, y no ganaría nada: el valor es el mismo.
 *
 * LO QUE ESTA VARIABLE **NO** HACE: no esconde el correo del paquete publicado.
 * El empaquetador sustituye toda `REACT_APP_*` por su valor literal, así que
 * acaba escrito en el archivo servido igual que si estuviera en duro. Lo que sí
 * evita es que viva en el CÓDIGO FUENTE del repo. Se aclara porque un letrero que
 * prometiera lo primero sería mentira (la misma corrección que ya está escrita en
 * `pages/SettingsPage.js`).
 */
export const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || '';

/** post-t7-1 (E1): la ruta a la que iba (camino mas consulta), del router; sin router (pruebas viejas, o un doble de
 *  react-router-dom sin `useLocation`), nada. La llamada va SIEMPRE y en el mismo orden: dentro de un router nunca
 *  lanza, y sin router lanza en TODOS los pintados (el router no aparece ni desaparece entre dos pintados de un
 *  guardia), asi que el orden de los hooks no cambia. La regla del build ve el `try` como condicion: por eso la
 *  excepcion de abajo, solo para esta linea. */
function useRutaActual() {
  let location = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    location = useLocation();
  } catch {
    location = null;
  }
  if (!location) return '';
  // post-t7-2b (D7 b): LA MISMA FUENTE QUE EL CIERRE DE SESION, la barra. El Panel limpia la consulta de la vuelta de
  // Stripe con `history.replaceState`, que el router no ve: el router seguia con `?credits_purchased=1&session_id=...`
  // y el cierre de sesion (AuthContext, que lee `window.location`) marcaba la ruta sin ella, asi que no casaban y la
  // ruta se guardaba para la siguiente persona. Cuando el router describe la barra (mismo camino), manda la barra; un
  // router que no la describe (uno en memoria, en pruebas) se lee como antes.
  const barra = typeof window !== 'undefined' ? window.location : null;
  if (barra && barra.pathname === location.pathname) return `${barra.pathname || ''}${barra.search || ''}`;
  return `${location.pathname || ''}${location.search || ''}`;
}

/** post-t7-1 (J1, #564): la bienvenida se carga al pintarse (y solo en la app): sus dependencias leen el objetivo del
 *  paquete al importarse, y los guardias de la web no las necesitan. */
function BienvenidaApp() {
  const Bienvenida = require('./BienvenidaApp').default;
  return <Bienvenida />;
}

/** post-t7-1 (J1, #564): las cuatro condiciones de la bienvenida (la cuarta, "sin sesion", la pone quien llama). */
export function esBienvenidaDeLaApp() {
  return IS_APP_BUILD && !hasSignedInBefore() && !hadGuestBefore();
}

/* post-t7-2c (F1, Anexo D.3): LA WEB AL RECARGAR PINTA EL ESQUELETO DE LA RUTA, NUNCA EL LOGO.
 *
 * `esqueleto` (opcional) es lo que la ruta pinta mientras la sesion resuelve: el chasis con la silueta de la
 * pantalla (App.js, `esqueletoDelArranque`). SOLO en la web; en la app sigue el logo de `AuthBootGate` y este guardia
 * devuelve null como siempre. Sin `esqueleto`, null tambien en la web: el fondo de la casa, sin spinner.
 *
 * Con `esqueleto`, lo que pinta antes y despues de resolver va dentro del MISMO proveedor de contexto: asi React
 * conserva el chasis (el mismo `<main>`) y, en las paginas del profe, la misma instancia de la pagina, que solo deja
 * de ser esqueleto y pide sus datos (`InstitutionalRoute`, abajo). Sin `esqueleto` el arbol es el de siempre. */
const EsqueletoDelArranque = React.createContext(false);

export function ProtectedRoute({ children, esqueleto }) {
  const { user, loading } = useAuth();
  const ruta = useRutaActual();
  const conEsqueleto = !IS_APP_BUILD && !!esqueleto;
  if (loading) {
    // post-t7-2c (F1): mientras la sesion resuelve no se navega ni se guarda nada (como siempre)
    return conEsqueleto ? <EsqueletoDelArranque.Provider value>{esqueleto}</EsqueletoDelArranque.Provider> : null;
  }
  // SIGNUP-first (F4.3b, solo app): en el build de Capacitor un usuario sin
  // sesión aterriza en /signup — la primera pantalla de una app INSTALADA HOY es
  // "crea tu cuenta", no "vuelve a entrar". El recíproco existe: /signup lleva
  // link a /login y viceversa. El flujo web no cambia (/login, como siempre).
  //
  // COS-22-R — Y DEJA DE SERLO EN CUANTO EL APARATO YA VIO UNA SESIÓN.
  //
  // Regla de Carlos: "está bien que de inicio te lleve a sign up, pero si ya has
  // iniciado sesión alguna vez e hiciste logout desde tu cuenta pues ahí sí que
  // te lleve a pantalla de sign in". La marca es local y monotónica (ver
  // lib/returningUser.js): arranque en frío de instalación nueva → /signup, como
  // hoy; a partir de la primera sesión → /login, para siempre.
  //
  // POR QUÉ EL FIX VIVE AQUÍ Y NO EN EL CALLER (lección del smoke en el iPhone,
  // donde el fix de COS-22 en Sidebar.js tenía tests verdes y seguía cayendo en
  // Sign up): ESTE guard es quien pinta la pantalla post-logout, y lo hace
  // ANTES que cualquier caller. `logout()` (AuthContext:747) hace `setUser(null)`
  // y DESPUÉS se sigue esperando lo lento — purga de bodega (plugins nativos,
  // segundos) y POST de revocación (techo de 10 s). El render con `user` en null
  // ocurre en ese mismo tick, así que este `<Navigate>` gana SIEMPRE, por
  // segundos, contra el `navigate()` que el caller ejecuta cuando la promesa por
  // fin resuelve. Además hay caminos de logout que NO navegan nada
  // (UserMenu.js:90 `onClick={logout}`, y la expiración de sesión de
  // AuthContext:175): para todos ellos este destino es el único que existe.
  // Arreglado aquí, la carrera deja de importar — gane quien gane, es /login.
  // post-t7-1 (E1): antes de mandarlo a entrar, se guarda a donde iba (solo si es una ruta valida)
  if (!user) {
    // post-t7-1 (F1, #580): "Start Free" llega aqui sin sesion con `start=1`; la pagina crea el invitado (en la web)
    // Remate de post-t7-2c (R2 punto 8): mientras crea el invitado, la web pinta el MISMO esqueleto de la espera
    // de la sesion (dentro del mismo proveedor), no el logo
    if (!IS_APP_BUILD && esArranqueDeInvitado(ruta)) {
      return <ArranqueDeInvitado search={ruta.slice(ruta.indexOf('?'))} esqueleto={conEsqueleto
        ? <EsqueletoDelArranque.Provider value>{esqueleto}</EsqueletoDelArranque.Provider> : null} />;
    }
    guardarRutaPendiente(ruta);
    // post-t7-1 (J1, #564): en la app, un telefono que nunca tuvo sesion ni invitado ve la bienvenida EN LUGAR de la
    // redireccion (no es una ruta: el gesto de atras desde "Iniciar sesion" la vuelve a mostrar)
    if (esBienvenidaDeLaApp()) return <BienvenidaApp />;
    return <Navigate to={IS_APP_BUILD && !hasSignedInBefore() ? '/signup' : '/login'} />;
  }
  // Require email verification for non-guest users
  // post-t7-2b (D7 a): la pestana tuvo una sesion (si luego vence sin cerrarse, su ruta no se guarda)
  notarSesion();
  if (!user.is_guest && !user.verified) return <Navigate to="/verify-email" />;
  // post-t7-1 (F1): con sesion, `start=1` no crea nada: Panel y la barra limpia (sin entrada en el historial)
  if (!IS_APP_BUILD && esArranqueDeInvitado(ruta)) return <Navigate to="/dashboard" replace />;
  // post-t7-1 (E1): ya con sesion en una ruta con sesion, la ruta guardada se uso (o ya no sirve): se borra
  borrarRutaPendiente();
  // post-t7-2c (F1): con esqueleto, dentro del mismo proveedor que el esqueleto (React conserva el chasis)
  return conEsqueleto ? <EsqueletoDelArranque.Provider value>{children}</EsqueletoDelArranque.Provider> : children;
}

/* TANDA 12 (firma #182) — EL MISMO CINTURÓN QUE EL VISOR, Y AHORA IMPORTA MÁS.
 *
 * El orden no se movió (sesión sin resolver → nada; sin sesión → login; cuenta
 * que no es la del administrador → dashboard). Lo que se añadió es el caso
 * degenerado: si el build no define la dirección, la constante queda en cadena
 * vacía y la comparación cruda `algo === ''` es VERDADERA para una cuenta con el
 * correo vacío — o sea que la puerta fallaba ABIERTA. Con la cadena vacía
 * descartada antes de comparar, sin dirección configurada NADIE es administrador.
 *
 * Es el mismo remate que la TANDA 11 le puso a `SettingsPage` y a
 * `DiagReadyChainPage`; aquí faltaba, y desde esta tanda estas dos rutas también
 * se registran en el build de app, así que la puerta se cruza desde el teléfono.
 * Honestidad sobre el alcance: ese correo vacío hoy NO existe (los cuatro altos
 * del servidor escriben uno no vacío, invitados incluidos, que reciben uno
 * sintético). Cuesta cuatro caracteres y deja el fallo en el sentido correcto.
 */
export function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const ruta = useRutaActual();
  if (loading) return null;
  // post-t7-2b (D7 c): las dos redirecciones REEMPLAZAN la entrada: con "Atras" no se vuelve a la ruta que rebota
  if (!user) { guardarRutaPendiente(ruta); return <Navigate to="/login" replace />; }
  notarSesion();   // post-t7-2b (D7 a)
  if (!ADMIN_EMAIL || user.email?.toLowerCase() !== ADMIN_EMAIL) return <Navigate to="/dashboard" replace />;
  return children;
}

/* PILOTO-1 (firma #79) — EL GUARD DE LA SUPERFICIE DE PROFESOR.
 *
 * Espejo exacto de `AdminRoute`, cambiando "¿eres Carlos?" por "¿traes la
 * bandera institucional?". La bandera llega en `/auth/me` (`is_institutional`,
 * SIEMPRE booleano: su ausencia en la base sale como false, que es el estado de
 * todas las cuentas que ya existen), así que una cuenta de hoy rebota a
 * /dashboard sin ver un pixel de la pantalla.
 *
 * ESTE GUARD NO ES EL CANDADO — es la mitad visible de un candado de DOS. La
 * otra vive en el servidor (backend/routes/groups.py), que contesta 403 aunque
 * alguien parchee este archivo en su navegador. Un candado de pantalla que se
 * salta con `curl` no es un candado.
 */
export function InstitutionalRoute({ children }) {
  const { user, loading } = useAuth();
  const ruta = useRutaActual();
  const enEsqueleto = React.useContext(EsqueletoDelArranque);
  if (loading) {
    // post-t7-2c (F1): dentro del esqueleto del arranque (solo web), la MISMA pagina con `soloEsqueleto`: pinta su
    // silueta sin pedir datos; al resolver, este guardia la devuelve sin la marca y la pagina pide lo suyo. Fuera del
    // esqueleto, null como siempre. Nunca navega mientras la sesion resuelve.
    if (enEsqueleto && !IS_APP_BUILD && React.isValidElement(children)) {
      return React.cloneElement(children, { soloEsqueleto: true });
    }
    return null;
  }
  if (!user) {
    guardarRutaPendiente(ruta);
    if (esBienvenidaDeLaApp()) return <BienvenidaApp />;   // post-t7-1 (J1)
    // post-t7-2b (D7 c): las dos redirecciones REEMPLAZAN la entrada: con "Atras" no se vuelve a la ruta que rebota
    return <Navigate to={IS_APP_BUILD && !hasSignedInBefore() ? '/signup' : '/login'} replace />;
  }
  notarSesion();   // post-t7-2b (D7 a)
  if (!user.is_institutional) return <Navigate to="/dashboard" replace />;
  return children;
}

export function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Allow guests to access login/signup pages so they can create a real account
  // post-t7-1 (E1): quien acaba de entrar llega a donde iba (si el guardia lo guardo); si no, al Panel
  if (user && !user.is_guest) return <Navigate to={destinoDespuesDeEntrar()} />;
  return children;
}

// Rutas web que NO dependen de la sesión para pintarse: se renderizan al
// instante y el overlay de arranque no debe taparlas — en especial "/", donde
// el HTML prerendered de la landing debe quedar visible sin interrupción (T5).
// post-t7-2b (A3): '/es' y '/es/precios', la portada y precios en espanol (pre-pintadas desde
// post-t7-2a), igual que '/' y '/pricing'.
const WEB_INSTANT_ROUTES = ['/', '/pricing', '/verify-email', '/es', '/es/precios'];
export const isWebInstantRoute = (pathname) =>
  WEB_INSTANT_ROUTES.includes(pathname) ||
  pathname.startsWith('/reset-password') ||
  pathname.startsWith('/auth/');

/**
 * AuthBootGate — pantalla de arranque del SPA (logo sobre #111111, igual que el
 * splash nativo: debe sentirse UN solo momento, no dos pantallas). Cubre el
 * hueco donde vivía el spinner de los guards mientras auth resuelve la sesión,
 * SOLO en la app desde post-t7-2c (ver abajo).
 */
// post-t7-2c (F1, Anexo D.3): LA WEB YA NO SE TAPA CON EL LOGO. `covered` es `IS_APP_BUILD`: en la web (siempre
// falso) mientras la sesion resuelve cada ruta pinta su esqueleto, o el fondo de la casa si no tiene (ver
// `ProtectedRoute`). La app: el logo, como hoy, byte a byte. LAPIDA: era `IS_APP_BUILD ||
// !isWebInstantRoute(pathname)` (la web autenticada se tapaba con el logo); `isWebInstantRoute` se queda exportada
// como la lista de las rutas que no dependen de la sesion.
export function AuthBootGate() {
  const { user, loading, bootOffline, retryBootRefresh } = useAuth();
  const covered = IS_APP_BUILD;
  // F6.α — boot nativo en pausa "sin conexión" (tokens del Keychain intactos,
  // refresh de arranque falló por red): pantalla honesta con Reintentar en vez
  // del logo mudo. Solo AuthContext (rama nativa) enciende bootOffline; en web
  // es siempre false.
  // F6.4b — si la bodega dejó ENTRAR (user poblado desde el snapshot local),
  // bootOffline sigue true solo como motor del retry de fondo: la app está
  // navegando y esta pantalla NO debe taparla. Solo cubre cuando el boot
  // quedó de verdad en pausa (sin user), con loading true debajo para que
  // los guards no naveguen.
  if (bootOffline && !user) return <OfflineBootScreen onRetry={retryBootRefresh} />;
  // post-t7-2c (F1): en la web no se monta (cero logo)
  if (!covered) return null;
  return <BootSplash active={loading && covered} />;
}
